import {announce} from '@github-ui/aria-live'
import {getQueryClient} from '@github-ui/query-client'
import {reactFetch} from '@github-ui/verified-fetch'
import {PencilIcon, TrashIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, FormControl, IconButton, Link, Portal, Spinner, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useCallback, useEffect, useId, useRef, useState} from 'react'

import styles from './SavedSearchesDialog.module.css'

const CUSTOM_SCOPES_PATH = '/search/custom_scopes'
const MAX_CUSTOM_SCOPES = 10
const NAME_MAX_LENGTH = 50
const QUERY_MAX_LENGTH = 500

interface SavedSearch {
  id: string | number
  name: string
  query: string
}

async function fetchSavedSearches(): Promise<SavedSearch[]> {
  const res = await reactFetch(CUSTOM_SCOPES_PATH, {method: 'GET', headers: {Accept: 'application/json'}})
  if (!res.ok) throw new Error(`Failed to load saved searches: ${res.status}`)
  return (await res.json()) as SavedSearch[]
}

/**
 * React port of the "Saved searches" (custom scopes) dialog that historically
 * lived on the legacy `<qbsearch-input>` element. Owns list / create / edit /
 * delete against `/search/custom_scopes`. Rendered by `SearchSubHeader` from
 * local state (like `ExperimentsDialog`) so it doesn't depend on the search bar.
 *
 * Note: mutating requests go through `reactFetch`, which requires the
 * `/search/custom_scopes` controller to opt into verified fetch.
 */
type SavedSearchesDialogInitialView = 'create' | 'manage-if-has-scopes'

export function SavedSearchesDialog({
  initialQuery,
  onClose,
  initialView = 'create',
  returnFocusRef,
}: {
  initialQuery: string
  onClose: () => void
  initialView?: SavedSearchesDialogInitialView
  returnFocusRef?: RefObject<HTMLElement | null>
}) {
  const [scopes, setScopes] = useState<SavedSearch[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [mode, setMode] = useState<'manage' | 'create'>(initialView === 'manage-if-has-scopes' ? 'manage' : 'create')
  const [editingId, setEditingId] = useState<string | number | null>(null)
  const [name, setName] = useState('')
  const [query, setQuery] = useState(initialQuery)
  const [error, setError] = useState<string | undefined>(undefined)
  const [deleteErrors, setDeleteErrors] = useState<Record<string | number, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const isDeletingRef = useRef(false)
  const errorId = useId()
  const formId = useId()

  const refresh = useCallback(async () => {
    try {
      const data = await fetchSavedSearches()
      setScopes(data)
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }, [])

  const invalidateQuickSearchCache = useCallback(() => {
    void getQueryClient().invalidateQueries({queryKey: [CUSTOM_SCOPES_PATH]})
  }, [])

  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const next = await fetchSavedSearches()
        if (active) {
          setScopes(next)
          if (initialView === 'manage-if-has-scopes' && next.length === 0) setMode('create')
        }
      } catch {
        if (active) setLoadError(true)
      }
    })()
    return () => {
      active = false
    }
  }, [initialView])

  const isEditing = editingId !== null
  // Keep create disabled until loading completes to prevent bypassing the limit
  const atLimit = mode === 'create' && !isEditing && (scopes === null || scopes.length >= MAX_CUSTOM_SCOPES)
  const hasScopes = (scopes?.length ?? 0) > 0

  const startCreate = useCallback(() => {
    setMode('create')
    setEditingId(null)
    setName('')
    setQuery('')
    setError(undefined)
  }, [])

  const startEdit = useCallback((scope: SavedSearch) => {
    setMode('create')
    setEditingId(scope.id)
    setName(scope.name)
    setQuery(scope.query)
    setError(undefined)
  }, [])

  const showManage = useCallback(() => {
    setMode('manage')
    setError(undefined)
  }, [])

  const save = useCallback(async () => {
    if (name.trim() === '' || query.trim() === '' || isSubmitting || atLimit) return
    setIsSubmitting(true)
    setError(undefined)
    const body = new FormData()
    if (isEditing) body.append('custom_scope_id', String(editingId))
    body.append('custom_scope_name', name)
    body.append('custom_scope_query', query)
    try {
      const res = await reactFetch(CUSTOM_SCOPES_PATH, {method: 'POST', body})
      if (!res.ok) {
        const msg = 'Could not save your search. Please check the name and query and try again.'
        setError(msg)
        announce(msg, {assertive: true})
        setIsSubmitting(false)
        return
      }
      // Save succeeded; invalidate QuickSearch cache and refresh local list.
      // If refresh fails, proceed to manage view anyway — the save did complete.
      invalidateQuickSearchCache()
      try {
        await refresh()
      } catch {
        // ignore refresh failure after a successful save
      }
      showManage()
    } catch {
      const msg = 'Could not save your search. Please check your connection and try again.'
      setError(msg)
      announce(msg, {assertive: true})
    }
    setIsSubmitting(false)
  }, [name, query, isEditing, editingId, isSubmitting, atLimit, refresh, showManage, invalidateQuickSearchCache])

  const remove = useCallback(
    async (id: string | number) => {
      if (isDeletingRef.current) return
      isDeletingRef.current = true
      setIsDeleting(true)
      setDeleteErrors(prev => {
        const next = {...prev}
        delete next[id]
        return next
      })
      try {
        const body = new FormData()
        body.append('id', String(id))
        body.append('_method', 'delete')
        const res = await reactFetch(CUSTOM_SCOPES_PATH, {method: 'POST', body})
        if (!res.ok) {
          const msg = 'Could not delete this saved search. Please try again.'
          setDeleteErrors(prev => ({...prev, [id]: msg}))
          announce(msg, {assertive: true})
          isDeletingRef.current = false
          setIsDeleting(false)
          return
        }
        invalidateQuickSearchCache()
        await refresh()
      } catch {
        const msg = 'Could not delete this saved search. Please check your connection and try again.'
        setDeleteErrors(prev => ({...prev, [id]: msg}))
        announce(msg, {assertive: true})
      }
      isDeletingRef.current = false
      setIsDeleting(false)
    },
    [refresh, invalidateQuickSearchCache],
  )

  const title = mode === 'manage' ? 'Saved searches' : isEditing ? 'Update saved search' : 'Create saved search'

  return (
    <Portal>
      <Dialog
        onClose={onClose}
        returnFocusRef={returnFocusRef}
        title={title}
        subtitle="Use saved searches to filter your results more quickly"
        width="large"
      >
        <Dialog.Body>
          {mode === 'create' ? (
            <form
              id={formId}
              onSubmit={e => {
                e.preventDefault()
                void save()
              }}
              className={styles.form}
            >
              {loadError && (
                <Flash variant="danger" role="alert">
                  Could not load your saved searches.{' '}
                  <Button type="button" variant="link" onClick={() => void refresh()}>
                    Try again
                  </Button>
                </Flash>
              )}
              {!loadError && atLimit && scopes !== null && (
                <Flash variant="warning" role="status">
                  Limit of {MAX_CUSTOM_SCOPES} saved searches reached. Please delete an existing saved search before
                  creating a new one.
                </Flash>
              )}
              {error && (
                <Flash id={errorId} variant="danger" role="alert">
                  {error}
                </Flash>
              )}
              <FormControl required>
                <FormControl.Label>Name</FormControl.Label>
                <TextInput
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={NAME_MAX_LENGTH}
                  characterLimit={NAME_MAX_LENGTH}
                  placeholder="github-ruby"
                  block
                  aria-describedby={error ? errorId : undefined}
                />
              </FormControl>
              <FormControl required>
                <FormControl.Label>Query</FormControl.Label>
                <TextInput
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  maxLength={QUERY_MAX_LENGTH}
                  characterLimit={QUERY_MAX_LENGTH}
                  placeholder="(repo:mona/a OR repo:mona/b) AND lang:python"
                  block
                  aria-describedby={error ? errorId : undefined}
                />
              </FormControl>
              <p className={styles.qualifierHint}>
                To see all available qualifiers, see our{' '}
                <Link
                  href="https://docs.github.com/search-github/github-code-search/understanding-github-code-search-syntax"
                  className="Link--inTextBlock"
                >
                  documentation
                </Link>
                .
              </p>
            </form>
          ) : scopes === null && !loadError ? (
            <div className={styles.loading}>
              <Spinner />
            </div>
          ) : loadError ? (
            <div className={styles.loadError}>
              <Flash variant="danger" role="alert">
                Could not load saved searches.{' '}
                <Button type="button" variant="link" onClick={() => void refresh()}>
                  Try again
                </Button>
              </Flash>
            </div>
          ) : hasScopes ? (
            <ul className={styles.list}>
              {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
              {scopes!.map(scope => (
                <li key={scope.id} className={styles.row}>
                  <div className={styles.rowText}>
                    <div className={styles.rowName}>{scope.name}</div>
                    <div className={styles.rowQuery}>{scope.query}</div>
                    {deleteErrors[scope.id] && (
                      <div className={styles.rowError} role="alert">
                        {deleteErrors[scope.id]}
                      </div>
                    )}
                  </div>
                  <IconButton
                    icon={PencilIcon}
                    aria-label={`Edit ${scope.name}`}
                    variant="invisible"
                    onClick={() => startEdit(scope)}
                  />
                  <IconButton
                    icon={TrashIcon}
                    aria-label={`Delete ${scope.name}`}
                    variant="danger"
                    disabled={isDeleting}
                    onClick={() => void remove(scope.id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.empty}>You don&apos;t have any saved searches yet.</p>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          {mode === 'create' ? (
            <Dialog.Buttons
              buttons={[
                {type: 'button', onClick: hasScopes ? showManage : onClose, content: 'Cancel'},
                {
                  type: 'submit',
                  form: formId,
                  content: isEditing ? 'Update saved search' : 'Create saved search',
                  buttonType: 'primary',
                  disabled: atLimit || name.trim() === '' || query.trim() === '',
                  loading: isSubmitting,
                  loadingAnnouncement: 'Saving saved search',
                },
              ]}
            />
          ) : (
            <Dialog.Buttons
              buttons={[
                {type: 'button', onClick: onClose, content: 'Cancel'},
                {type: 'button', onClick: startCreate, content: 'Create saved search', buttonType: 'primary'},
              ]}
            />
          )}
        </Dialog.Footer>
      </Dialog>
    </Portal>
  )
}
